import i18nFactory from '@yandex-int/i18n';
import { toPath } from 'lodash';

import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { BannerId } from '../../typings/banners';
import {
    EditedBannerGroup,
    ProtocolValue
} from '../../typings/bannersEditState';
import { ValidationErrorData } from '../../typings/common';
import { AdvType } from '../../typings/domain';
import { InternalResourceId } from '../../typings/internalBanners';
import {
    EditedInternalBanner,
    EditedInternalBannerBase,
    EditedInternalBannerCampaign
} from '../../typings/internalBannersEditState';
import { State } from '../../typings/state';
import { Omit } from '../../typings/utils';
import { internalBannerFieldErrorMapper } from '../BannerEditErrors/BannerEditErrors';
import { InternalBannersErrorsMapper } from '../BannerEditErrors/ErrorsTextMapper/ErrorsTextMapper';
import { InternalBannerValidator } from '../BannerEditValidation/InternalBannerValidator';
import { getBannerEditPageItemEditorId } from '../BannerHelpers/BannerHelpers';
import {
    convertServerPathToClient,
    mapTextToValidationErrorData
} from '../ErrorsMapper/ErrorsMapper';

import * as keyset from './InternalBannerHelpers.i18n';

const i18n = i18nFactory(keyset);

export const getInternalBannerDescriptionEditorValidationResult = (
    state: State,
    bannerId: string
): ValidationErrorData[] => {
    const { editPageItemNameEditor: editors } = state;
    const editorId = getBannerEditPageItemEditorId(bannerId);
    const editorData = selectEditPageItemNameEditorValue(editors, editorId);

    return getInternalBannerDescriptionValidationResult(
        state,
        bannerId,
        editorData.value
    );
};

export const getInternalBannerDescriptionValidationResult = (
    state: State,
    bannerId: string,
    description: string
): ValidationErrorData[] => {
    const {
        editInternalBanners: { banners }
    } = state;

    const banner = banners[bannerId];
    const errors = InternalBannerValidator.validateDescription({
        ...banner,
        description
    });

    const clientValidationItems = convertServerPathToClient(
        errors,
        internalBannerFieldErrorMapper
    );

    return mapTextToValidationErrorData(
        clientValidationItems,
        InternalBannersErrorsMapper
    );
};

export const splitInternalBannersOnNewAndEdited = (
    state: State,
    banners: Record<BannerId, EditedInternalBanner>
): {
    editedBanners: Record<string, EditedInternalBanner>;
    newBanners: Record<string, EditedInternalBanner>;
} => {
    const { editPageItemNameEditor: editors } = state;

    return Object.keys(banners).reduce(
        (result, bannerId) => {
            const banner = { ...banners[bannerId] };

            if (banner.isSaved) {
                return result;
            }

            const editorData = selectEditPageItemNameEditorValue(
                editors,
                getBannerEditPageItemEditorId(bannerId)
            );

            if (editorData.isVisible) {
                banner.description = editorData.value;
            }

            if (banners[bannerId].isNew) {
                result.newBanners[bannerId] = banner;
            } else if (!banners[bannerId].markedForDeletion) {
                result.editedBanners[bannerId] = banner;
            }

            return result;
        },
        { editedBanners: {}, newBanners: {} }
    );
};

export const isInternalBannerCheck = (bannerType: AdvType): boolean =>
    [AdvType.INTERNAL].includes(bannerType);

export const splitUrlWithSchema = (
    value: string
): { url: string; schema: ProtocolValue | undefined } => {
    let schema: ProtocolValue | undefined;

    if (value.startsWith(ProtocolValue.http)) {
        schema = ProtocolValue.http;
    }

    if (value.startsWith(ProtocolValue.https)) {
        schema = ProtocolValue.https;
    }

    return {
        schema,
        url: schema ? value.replace(schema, '') : value
    };
};

export const getResourceFieldErrorText = (
    index: number,
    templateVarsErrors: ValidationErrorData[]
): ValidationErrorData[] | undefined => {
    const errors: ValidationErrorData[] = [];

    templateVarsErrors.forEach(error => {
        const errPath = toPath(error.path);

        if (errPath[errPath.length - 1] === index.toString()) {
            errors.push(error);
        }
    });

    return errors;
};

export const getResourceFieldImagesErrorText = (
    resourceId: InternalResourceId,
    imageErrors: ValidationErrorData[]
): ValidationErrorData[] | undefined => {
    const errors: ValidationErrorData[] = [];

    imageErrors.forEach(error => {
        const errPath = toPath(error.path);

        if (errPath[errPath.length - 1] === resourceId) {
            errors.push(error);
        }
    });

    return errors;
};

const getNewInternalBaseData = (
    group: EditedBannerGroup,
    campaign: EditedInternalBannerCampaign
): Omit<EditedInternalBannerBase, 'id' | 'bannerType' | 'isNew'> => ({
    isMobile: false,
    isSaved: false,
    isEditAllow: true,
    markedForDeletion: false,
    canBeDeleted: true,
    readOnly: false,
    campaign,
    group,
    errors: undefined,
    status: {
        isPreviousVersionShown: false,
        isRejectedOnModeration: false
    }
});

const makeNewEditedBannerTypeInternal = (
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedInternalBannerCampaign,
    initialData: Partial<EditedInternalBanner>
): EditedInternalBanner => {
    return {
        id,
        ...getNewInternalBaseData(group, campaign),
        isNew,
        description: i18n('new-banner'),
        bannerType: AdvType.INTERNAL,
        templateVars: [],
        templateId: '0',

        ...initialData
    };
};

export const makeNewInternalEditedBanner = (
    id: string,
    isNew: boolean,
    group: EditedBannerGroup,
    campaign: EditedInternalBannerCampaign,
    initialData: Partial<EditedInternalBanner> = {}
): EditedInternalBanner => {
    return makeNewEditedBannerTypeInternal(
        id,
        isNew,
        group,
        campaign,
        initialData
    );
};
